/**
 * TeamPickerModal — Pick a team to assign verified leads to.
 *
 * Apr 28, 2026 (Squad-D). Screen 05 of 12. Used by VerifiedPoolPage.
 *
 * - Search input · region-match toggle
 * - Team cards with: head avatar, name, region, member count, open-lead load,
 *   weekly conversion %, "Assign to Head" + "Pick exec…" buttons
 * - Visual conflict warnings (high load, region mismatch)
 *
 * Backend:
 *  - POST /leads/:id/assign body { teamId, assignToUserId? }
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Check, ChevronLeft } from 'lucide-react';
import { api } from '@/utils/api';
import { Avatar, type Team } from './_lmShared';

interface TeamMember {
  id: string; // membership id
  userId: string;
  level: 'TEAM_HEAD' | 'BACKEND_STAFF' | 'FIELD_EXECUTIVE' | 'DEPARTMENT_HEAD' | string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string | null;
  };
}

export function TeamPickerModal({
  leadIds,
  teams,
  onClose,
  onAssigned,
  currentRegion,
}: {
  leadIds: string[];
  teams: Team[];
  onClose: () => void;
  onAssigned: () => void;
  currentRegion?: string;
}) {
  const [query, setQuery] = useState('');
  const [regionMatch, setRegionMatch] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);

  // Apr 28 evening — Two-step picker. Step 1: pick team. Step 2: pick exec
  // within that team. Both use the same modal frame; step 2 has a back btn.
  const [step, setStep] = useState<'team' | 'exec'>('team');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = teams;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.headName ?? '').toLowerCase().includes(q) ||
          (t.region ?? '').toLowerCase().includes(q),
      );
    }
    if (regionMatch && currentRegion) {
      list = list.filter((t) => t.region === currentRegion);
    }
    return list;
  }, [teams, query, regionMatch, currentRegion]);

  // Load team members when stepping to the exec picker.
  useEffect(() => {
    if (step !== 'exec' || !selectedTeam) return;
    setMembersLoading(true);
    setMembersError(null);
    api
      .get(`/teams/${selectedTeam.id}/members`)
      .then((res) => {
        const list: TeamMember[] = res.data?.members ?? res.data?.data ?? res.data ?? [];
        setMembers(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        setMembersError(
          err.response?.data?.error || 'Failed to load team members',
        );
      })
      .finally(() => setMembersLoading(false));
  }, [step, selectedTeam]);

  function startExecPick(team: Team) {
    setSelectedTeam(team);
    setStep('exec');
    setMembers([]);
  }

  function backToTeams() {
    setStep('team');
    setSelectedTeam(null);
  }

  async function assignToTeamHead(team: Team) {
    if (leadIds.length === 0) return;
    setSubmitting(team.id);
    try {
      for (const id of leadIds) {
        await api.post(`/lead-integrations/leads/${id}/assign`, {
          teamId: team.id,
          assignToUserId: team.headId ?? null,
        });
      }
      onAssigned();
    } catch (err) {
      alert('Assign failed — backend not yet ready, or you do not have permission.');
    } finally {
      setSubmitting(null);
    }
  }

  async function assignToMember(team: Team, m: TeamMember) {
    if (leadIds.length === 0) return;
    setSubmitting(`${team.id}:${m.userId}`);
    try {
      for (const id of leadIds) {
        await api.post(`/lead-integrations/leads/${id}/assign`, {
          teamId: team.id,
          assignToUserId: m.userId,
        });
      }
      onAssigned();
    } catch (err) {
      alert('Assign failed — backend not yet ready, or you do not have permission.');
    } finally {
      setSubmitting(null);
    }
  }

  // Group members by level for the exec picker (Team Head first, then
  // Backend Staff, then Field Executives) — matches the role hierarchy
  // user locked Apr 28 mid-day.
  const membersByLevel = useMemo(() => {
    const order = ['TEAM_HEAD', 'DEPARTMENT_HEAD', 'BACKEND_STAFF', 'FIELD_EXECUTIVE'];
    const groups: Record<string, TeamMember[]> = {};
    for (const m of members) {
      const k = m.level || 'OTHER';
      (groups[k] ??= []).push(m);
    }
    return order
      .map((k) => ({ level: k, members: groups[k] || [] }))
      .filter((g) => g.members.length > 0);
  }, [members]);

  function levelLabel(level: string): string {
    if (level === 'TEAM_HEAD') return 'Team Head';
    if (level === 'DEPARTMENT_HEAD') return 'Department Head';
    if (level === 'BACKEND_STAFF') return 'Backend Staff';
    if (level === 'FIELD_EXECUTIVE') return 'Field Executive';
    return level.replace(/_/g, ' ').toLowerCase();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27,23,19,.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="aura-root"
        style={{
          width: 720,
          maxWidth: '100%',
          maxHeight: '88vh',
          background: 'var(--ivory-0)',
          borderRadius: 14,
          boxShadow: 'var(--sh-4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid var(--line)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {step === 'exec' && (
            <button
              className="btn btn-ghost"
              onClick={backToTeams}
              title="Back to teams"
              style={{ marginRight: 10 }}
            >
              <ChevronLeft size={14} />
            </button>
          )}
          <div>
            <div style={{ fontFamily: 'var(--ff-serif)', fontWeight: 500, fontSize: 18 }}>
              {step === 'team'
                ? 'Step 1 — Pick a team'
                : `Step 2 — Pick executive in ${selectedTeam?.name ?? 'team'}`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Assigning{' '}
              <span style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
                {leadIds.length} lead{leadIds.length === 1 ? '' : 's'}
              </span>
              {step === 'exec' && selectedTeam && (
                <>
                  {' '}· <span style={{ color: 'var(--ink-2)' }}>Team {selectedTeam.name}</span>
                </>
              )}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        {/* Search bar (team step only) */}
        {step === 'team' && (
          <div
            style={{
              padding: 14,
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search teams or members…"
              style={{
                flex: 1,
                height: 32,
                padding: '0 10px',
                border: '1px solid var(--line)',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: 13,
                background: 'var(--ivory-1)',
                color: 'var(--ink-0)',
                outline: 'none',
              }}
            />
            {currentRegion && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setRegionMatch((v) => !v)}
                style={{
                  background: regionMatch ? 'var(--forest-50)' : undefined,
                  color: regionMatch ? 'var(--forest-600)' : undefined,
                }}
              >
                Region match {regionMatch && <Check size={11} />}
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {step === 'team' &&
            (filtered.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                {teams.length === 0
                  ? 'No teams available. Ask your admin to create teams from Employees → Teams tab.'
                  : 'No teams match your filter.'}
              </div>
            ) : (
              filtered.map((t, i) => {
                const high = (t.openLeads ?? 0) > 40;
                const matchRegion = currentRegion && t.region === currentRegion;
                return (
                  <div
                    key={t.id}
                    style={{
                      padding: '14px 18px',
                      borderBottom: i === filtered.length - 1 ? 'none' : '1px solid var(--ivory-3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}
                  >
                    <Avatar name={t.headName ?? t.name} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</span>
                        {high && (
                          <span className="chip chip-red" title="High load">
                            {t.openLeads} open · high
                          </span>
                        )}
                        {matchRegion && <span className="chip chip-green">Region match</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        Head: {t.headName ?? '—'} · {t.memberCount ?? 0} members ·{' '}
                        {t.region ?? '—'}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          marginTop: 6,
                          fontSize: 11,
                          color: 'var(--ink-3)',
                        }}
                      >
                        <span>
                          <span style={{ color: 'var(--ink-0)', fontWeight: 500 }} className="num">
                            {t.openLeads ?? 0}
                          </span>{' '}
                          open leads
                        </span>
                        <span>·</span>
                        <span>
                          <span
                            style={{ color: 'var(--forest-600)', fontWeight: 500 }}
                            className="num"
                          >
                            {t.weeklyConversion ?? 0}%
                          </span>{' '}
                          wk conversion
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => assignToTeamHead(t)}
                        disabled={submitting !== null}
                      >
                        {submitting === t.id ? 'Assigning…' : 'Assign to Head'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => startExecPick(t)}
                        disabled={submitting !== null}
                      >
                        Pick exec…
                      </button>
                    </div>
                  </div>
                );
              })
            ))}

          {step === 'exec' && (
            <div>
              {membersLoading && (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                  Loading team members…
                </div>
              )}
              {membersError && (
                <div style={{ padding: 24, color: 'var(--coral-600)', fontSize: 13 }}>
                  {membersError}
                </div>
              )}
              {!membersLoading && !membersError && members.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                  No members in this team yet. Add members from Employees → Teams.
                </div>
              )}
              {!membersLoading && !membersError && membersByLevel.map((group) => (
                <div key={group.level}>
                  <div
                    style={{
                      padding: '10px 18px 6px',
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--ink-3)',
                      background: 'var(--ivory-1)',
                      borderBottom: '1px solid var(--ivory-3)',
                    }}
                  >
                    {levelLabel(group.level)} · {group.members.length}
                  </div>
                  {group.members.map((m) => {
                    const isAssigning = submitting === `${selectedTeam!.id}:${m.userId}`;
                    return (
                      <div
                        key={m.id}
                        style={{
                          padding: '12px 18px',
                          borderBottom: '1px solid var(--ivory-3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                        }}
                      >
                        <Avatar name={`${m.user.firstName} ${m.user.lastName}`} size={32} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 13.5 }}>
                            {m.user.firstName} {m.user.lastName}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                            {m.user.email}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => assignToMember(selectedTeam!, m)}
                          disabled={submitting !== null}
                        >
                          {isAssigning ? 'Assigning…' : 'Assign'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 14,
            borderTop: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            background: 'var(--ivory-1)',
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Tip: assigning to Head delegates distribution. Pick exec for direct ownership.
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamPickerModal;
